import { Check } from "lucide-react";
import { useState } from "react";
import type { ReactNode } from "react";

import { PortalHead } from "@/features/portal/components/portal-chrome";
import { Accent, GoLink, glass } from "@/features/portal/components/portal-ui";
import { StoreButtons } from "@/features/portal/components/store-buttons";
import { photos } from "@/features/portal/content/photos";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const perks = () => [m.portal_perk_coach(), m.portal_perk_plan(), m.portal_perk_diet(), m.portal_perk_app()];

const lede = "max-w-[480px] text-lg leading-[1.5] tracking-[-0.01em] text-muted-foreground";

const MODES = [
  { id: "solo", label: () => m.portal_hero_mode_solo() },
  { id: "coach", label: () => m.portal_hero_mode_coach() },
] as const;

type Mode = (typeof MODES)[number]["id"];

/** What the app is for someone training this way. Every panel stays laid out, so the hero never jumps. */
const ModePanel = ({ active, children }: { active: boolean; children: ReactNode }) => (
  <div aria-hidden={!active} className={cn("col-start-1 row-start-1 flex flex-col gap-6", !active && "invisible")}>
    {children}
  </div>
);

/**
 * The front door: a lifter in black and white fading into the head card, and beside her the two
 * ways the app is used. The switch says what each one means and where it goes next: get the app
 * and train solo, or find a specialist below. What the app holds is listed underneath.
 */
export const PortalHero = () => {
  const [mode, setMode] = useState<Mode>("solo");

  return (
    <PortalHead className="flex min-h-[640px] flex-col md:min-h-[min(860px,calc(100svh-24px))] md:pb-[30px]">
      <img
        src={photos.squatMono}
        alt=""
        fetchPriority="high"
        className="absolute inset-0 -z-20 size-full object-cover object-[70%_30%] opacity-70 grayscale [mask-image:linear-gradient(0deg,transparent_15%,#000_75%)] lg:left-[38%] lg:w-[62%] lg:opacity-100 lg:[mask-image:linear-gradient(90deg,transparent,#000_40%)]"
      />
      <span
        aria-hidden
        className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_75%_60%,color-mix(in_oklch,var(--primary)_14%,transparent),transparent_70%)]"
      />

      <div className="flex max-w-[760px] flex-1 flex-col justify-end pt-36 pb-10 md:pl-[clamp(0px,4vw,64px)] lg:justify-center lg:pt-12">
        <h1 className="text-[min(9.5vw,2.875rem)] leading-[0.96] sm:text-[clamp(2.875rem,6.9vw,6.75rem)] font-medium tracking-[-0.05em] lg:whitespace-nowrap">
          {m.portal_hero_title_lead()}
          <br />
          <Accent>{m.portal_hero_title_tail()}</Accent>
        </h1>
        <div
          role="group"
          aria-label={m.portal_hero_mode_label()}
          className={cn("mt-6 inline-flex w-fit max-w-full gap-1 rounded-full p-1 md:mt-8", glass, "shadow-none")}
        >
          {MODES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              aria-pressed={mode === id}
              onClick={() => setMode(id)}
              className="h-10 rounded-full px-[18px] text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none aria-pressed:bg-foreground aria-pressed:text-background"
            >
              {label()}
            </button>
          ))}
        </div>
        <div className="mt-6 grid">
          <ModePanel active={mode === "solo"}>
            <p className={lede}>{m.portal_hero_solo_body()}</p>
            <StoreButtons />
          </ModePanel>
          <ModePanel active={mode === "coach"}>
            <p className={lede}>{m.portal_hero_coach_body()}</p>
            <GoLink to="#specialists" tone="lime" className="w-fit">
              {m.portal_specialists_title()}
            </GoLink>
          </ModePanel>
        </div>
      </div>

      <ul className="flex flex-wrap gap-2 md:ml-[clamp(0px,4vw,64px)]">
        {perks().map((perk) => (
          <li
            key={perk}
            className={cn(
              "inline-flex h-[38px] items-center gap-2 rounded-full pr-4 pl-3 text-[13px] font-medium",
              glass,
              "shadow-none",
            )}
          >
            <Check aria-hidden className="size-4 text-[var(--site-ink)]" />
            {perk}
          </li>
        ))}
      </ul>
    </PortalHead>
  );
};
